import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { Anchor, Card, Flex, Text } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { type CoreBooking } from '@flecto/types'

import {
  AvatarProductsGroup,
  BadgeBookingPaymentRentalStatus,
  BadgeBookingStatus,
  Icon,
  TextDate,
} from '..'
import { useMediaQuery } from '../../src'

type CardBookingInfosProps = {
  booking: CoreBooking
}

export function CardBookingInfos({ booking }: CardBookingInfosProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'bookings' })
  const { isDesktop } = useMediaQuery()

  const products = (booking.booking_products ?? []).map((bp) => bp.product)

  const BookingIdSection = ({ compact = false }: { compact?: boolean }) => (
    <Flex direction='column' gap={compact ? 'xs' : 'sm'}>
      <Text c='dimmed'>{t('booking')}</Text>
      <Anchor fw={500} href={`/bookings/${booking.internal_id}`} c='brand'>
        #{booking.internal_id}
      </Anchor>
    </Flex>
  )

  const BadgesSection = ({
    compact = false,
    align = 'flex-start',
  }: {
    compact?: boolean
    align?: 'flex-start' | 'flex-end'
  }) => (
    <Flex direction='column' align={align} gap={compact ? 'xs' : 'sm'}>
      <BadgeBookingStatus booking={booking} />
      <BadgeBookingPaymentRentalStatus booking={booking} withDot />
    </Flex>
  )

  const CustomerDatesSection = ({
    compact = false,
    flex = false,
  }: {
    compact?: boolean
    flex?: boolean
  }) => (
    <Flex
      direction='column'
      gap={compact ? 'xs' : 'sm'}
      style={flex ? { flex: 1 } : undefined}
    >
      <Text fw={500} lineClamp={compact ? 1 : undefined}>
        {booking?.customer?.full_name}
      </Text>
      <Flex align='center' gap='xs' wrap={compact ? 'wrap' : 'nowrap'}>
        <TextDate
          c='dimmed'
          date={booking?.start_at}
          variant='datetime-brief'
        />
        <Icon icon={faArrowRight} size='xs' color='gray.7' />
        <TextDate c='dimmed' date={booking?.end_at} variant='datetime-brief' />
      </Flex>
    </Flex>
  )

  const AvatarSection = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
    <AvatarProductsGroup products={products} size={size} />
  )

  return (
    <Card p={isDesktop ? 'lg' : 'md'}>
      <Flex
        direction={isDesktop ? 'row' : 'column'}
        gap={isDesktop ? 'xl' : 'md'}
        justify={isDesktop ? 'space-between' : undefined}
        align={isDesktop ? 'center' : undefined}
      >
        {isDesktop ? (
          <>
            <Flex gap='xl' align='center'>
              <BookingIdSection />
              <BadgesSection />
              <CustomerDatesSection />
            </Flex>
            <AvatarSection />
          </>
        ) : (
          <>
            <Flex justify='space-between' align='flex-start'>
              <BookingIdSection compact />
              <BadgesSection compact align='flex-end' />
            </Flex>
            <Flex justify='space-between' align='flex-end'>
              <CustomerDatesSection compact flex />
              <AvatarSection size='sm' />
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  )
}
